import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, Clock, Check, XCircle, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { ClaimTimeline } from "../claims/ClaimTimeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: { badge: "bg-amber-500/20 text-amber-300 border-amber-500/30", icon: Clock, label: "PENDING REVIEW" },
  in_review: { badge: "bg-blue-500/20 text-blue-300 border-blue-500/30", icon: Clock, label: "IN REVIEW" },
  approved: { badge: "bg-green-500/20 text-green-400 border-green-500/30", icon: Check, label: "APPROVED" },
  gifted: { badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", icon: Check, label: "PACK GIFTED" },
  rejected: { badge: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle, label: "REJECTED" },
};

export const MyClaimsStatus = ({ token, refreshKey = 0 }) => {
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchClaims = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/claims/my-claims`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setClaims([]);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims, refreshKey]);

  if (!token || claims === null || claims.length === 0) return null;

  return (
    <div className="max-w-2xl mx-auto mb-16" data-testid="my-claims-section">
      <div className="relative">
        <div className="absolute -inset-[1px] bg-gradient-to-b from-amber-500/25 to-transparent rounded-2xl"></div>
        <div className="relative bg-[#0c0c0c] rounded-2xl border border-white/[0.04] overflow-hidden">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4"
            data-testid="my-claims-toggle"
          >
            <div className="flex items-center gap-2.5">
              <FileText size={16} className="text-amber-400" />
              <span className="text-white font-black text-sm uppercase tracking-wider">My Claims</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                {claims.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                onClick={(e) => { e.stopPropagation(); fetchClaims(); }}
                className="p-1.5 rounded-lg hover:bg-white/5 cursor-pointer"
                data-testid="my-claims-refresh"
                title="Refresh"
              >
                <RefreshCw size={13} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
              </span>
              {expanded ? <ChevronUp size={15} className="text-gray-500" /> : <ChevronDown size={15} className="text-gray-500" />}
            </div>
          </button>

          {expanded && (
            <div className="px-5 pb-5 space-y-3">
              {claims.map((claim) => {
                const style = STATUS_STYLES[claim.status] || STATUS_STYLES.pending;
                const StatusIcon = style.icon;
                return (
                  <div
                    key={claim.id}
                    className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4"
                    data-testid={`my-claim-${claim.serial_number}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="text-white font-mono font-bold text-sm">#{claim.serial_number}</span>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-2.5 py-1 rounded-full border ${style.badge}`}>
                        <StatusIcon size={11} /> {style.label}
                      </span>
                    </div>
                    <ClaimTimeline claim={claim} />
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>{claim.marketplace}</span>
                      <span>{new Date(claim.created_at).toLocaleDateString()}</span>
                      {claim.collectible_name && (
                        <span className="text-amber-300/90">Matched: {claim.collectible_name}</span>
                      )}
                    </div>
                    {claim.status === "pending" && (
                      <p className="mt-2 text-[11px] text-gray-500">
                        Our team is verifying your receipt — usually within 24–48 hours.
                      </p>
                    )}
                    {claim.status === "approved" && (
                      <Link
                        to="/locker"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-green-400 hover:text-green-300"
                        data-testid={`my-claim-locker-link-${claim.serial_number}`}
                      >
                        View in your Locker →
                      </Link>
                    )}
                    {claim.status === "gifted" && (
                      <Link
                        to="/locker"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300"
                        data-testid={`my-claim-gift-link-${claim.serial_number}`}
                      >
                        🎁 A sealed {claim.gifted_pack?.drop_name || ""} pack is in your Locker — rip it! →
                      </Link>
                    )}
                    {claim.status === "rejected" && claim.admin_notes && (
                      <p className="mt-2 text-[11px] text-red-400/80">Reason: {claim.admin_notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyClaimsStatus;
